import { useNavigate } from '@tanstack/react-router';
import { format } from 'date-fns';
import { useAtom } from 'jotai';
import { Badge } from 'primereact/badge';
import { Button } from 'primereact/button';
import { OverlayPanel } from 'primereact/overlaypanel';
import React, { useMemo, useRef } from 'react';
import { Notification, notificationsAtom } from '~/atoms/workflowsSimpleAtom';
import { Spinner } from '~/components/ui/common/Spinner';

interface ExtendedNotification extends Notification {
  id: string;
  subject: string;
  sender: string;
  receivedAt: Date;
  workflowId: string;
  completed: boolean;
  failed: boolean;
  isNew: boolean;
}

export const NotificationBell = React.memo(() => {
  const [notifications] = useAtom(notificationsAtom);

  const overlayPanelRef = useRef<OverlayPanel>(null);

  const navigate = useNavigate();

  const convertedNotifications = useMemo((): ExtendedNotification[] => {
    return notifications.notifications
      .map((notification) => ({
        ...notification,
        id: notification.workflow_id,
        subject:
          notification.title || notification.data.subject || 'No subject',
        sender: notification.data.from_address || 'System',
        receivedAt: new Date(notification.started_at),
        workflowId: notification.workflow_id,
        completed: notification.status === 'completed',
        failed: notification.status === 'failed',
        isNew:
          notification.status === 'completed' ||
          notification.status === 'failed',
      }))
      .sort((a, b) => b.receivedAt.getTime() - a.receivedAt.getTime())
      .slice(0, 10);
  }, [notifications.notifications]);

  const processingCount = useMemo(
    () =>
      convertedNotifications.filter(
        (notification) => !notification.completed && !notification.failed,
      ).length,
    [convertedNotifications],
  );

  const handleNotificationClick = (notification: ExtendedNotification) => {
    if (notification.workflowId) {
      navigate({
        to: '/workflows/$workflowId',
        params: { workflowId: notification.workflowId },
      });
    }
  };

  return (
    <div className="notification-bell-container inline-flex align-items-center">
      <Button
        icon="pi pi-bell"
        rounded
        text
        severity="secondary"
        className="p-button-text-secondary notification-bell-button"
        onClick={(e) => overlayPanelRef.current?.toggle(e)}
        aria-label="Notifications"
      />
      {processingCount > 0 && (
        <Badge
          value={processingCount}
          severity="info"
          className="notification-count"
        />
      )}

      <OverlayPanel ref={overlayPanelRef} style={{ width: '320px' }}>
        <div className="p-1">
          {notifications.error && (
            <div className="p-2 text-center text-red-500 mb-2">
              <i className="pi pi-exclamation-triangle mr-2"></i>
              {notifications.error}
            </div>
          )}

          {notifications.isLoading ? (
            <div className="p-2 text-center">
              <i
                className="pi pi-spin pi-spinner"
                style={{ fontSize: '1.5rem' }}
              ></i>
            </div>
          ) : convertedNotifications.length === 0 ? (
            <div className="p-2 text-center">No notifications</div>
          ) : (
            <div className="notification-list">
              {convertedNotifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`py-1 px-2 notification-item cursor-pointer hover:surface-hover ${notification.isNew ? 'notification-new' : ''}`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="flex justify-content-between align-items-center">
                    <span className="font-medium text-sm">
                      {notification.subject}
                    </span>
                    {notification.status === 'starting' ? (
                      <i
                        className="pi pi-envelope text-blue-500"
                        style={{ fontSize: '1rem' }}
                      ></i>
                    ) : notification.status === 'running' ? (
                      <Spinner size={16} />
                    ) : notification.status === 'completed' ? (
                      <i
                        className="pi pi-check-circle text-green-500"
                        style={{ fontSize: '1rem' }}
                      ></i>
                    ) : (
                      <i
                        className="pi pi-times-circle text-red-500"
                        style={{ fontSize: '1rem' }}
                      ></i>
                    )}
                  </div>
                  <div className="text-xs text-color-secondary mt-1">
                    From: {notification.sender}
                  </div>
                  <div className="text-xs text-color-secondary">
                    {format(notification.receivedAt, 'MMM dd, yyyy HH:mm')}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </OverlayPanel>
    </div>
  );
});
